import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";
import type { RiskGrade } from "./state-of-play";

export interface SimulatorInputs {
  /** office_number. Undefined = "All Offices". Together with industry it picks the defaults the simulator starts
   *  from -- the same FTP / expected loss / external commission and policy values Manage Targets shows. */
  office?: number;
  /** Undefined = "All Industry". */
  industry?: string;
  /** Undefined = "All Ratings". Only the policy (opex, target margin, commission rate) can be set per rating;
   *  FTP, expected loss and external commission have no rating dimension, same as Manage Targets. */
  grade?: RiskGrade;
  financedAmount: number;
  termMonths: number;
}

export interface SimulatorResult {
  ftp: number;
  expectedLoss: number;
  opex: number;
  /** Read-only: paid to the vendor / dealer, outside the margin. */
  externalCommission: number;
  /** Editable in the simulator: percent of gross margin paid to the RM. */
  internalCommissionRate: number;
  targetNetMargin: number;
  /** targetNetMargin / (1 - internalCommissionRate) */
  grossMargin: number;
  /** ftp + expectedLoss + opex + externalCommission + grossMargin */
  requiredYield: number;
  /** grossMargin - targetNetMargin, in percent of the deal */
  internalCommission: number;
  internalCommissionDollars: number;
  grossRav: number;
  netRav: number;
  wal: number;
  projectedLifetimeLoss: { pct: number; dollars: number };
}

// Office and industry (sic_division) options are fetched live via
// useOffices()/useIndustries() in ./reference -- no fixed list.
export const PLANNING_GRADES: RiskGrade[] = ["A", "B1", "B2", "B3"];
export const PLANNING_TERMS = [24, 36, 48, 60, 72, 84];

export const ALL_OFFICES_LABEL = "All Offices";
export const ALL_INDUSTRIES_LABEL = "All Industry";
export const ALL_RATINGS_LABEL = "All Ratings";

/**
 * Level-payment amortization schedule; weighted average life is the
 * principal-weighted average timing of principal repayment, in years.
 * Same client-side calculation used elsewhere in this build.
 */
export function computeWal(financedAmount: number, termMonths: number, annualRatePct: number) {
  if (financedAmount <= 0 || termMonths <= 0) return 0;
  const i = annualRatePct / 100 / 12;
  const payment =
    i === 0
      ? financedAmount / termMonths
      : (financedAmount * i) / (1 - Math.pow(1 + i, -termMonths));

  let balance = financedAmount;
  let weighted = 0;
  let principalPaid = 0;

  for (let m = 1; m <= termMonths; m += 1) {
    const interest = balance * i;
    const principal = Math.min(balance, Math.max(payment - interest, 0));
    weighted += principal * (m / 12);
    principalPaid += principal;
    balance -= principal;
    if (balance <= 0.01) break;
  }

  if (principalPaid <= 0) return 0;
  return Number((weighted / principalPaid).toFixed(2));
}

export const planningQueryKeys = {
  simulator: (inputs: SimulatorInputs | null, ticket?: TicketSize) =>
    ["planning", "simulator", inputs, ticket ?? "all"] as const,
};

export function useSimulatorResult(inputs: SimulatorInputs | null) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: planningQueryKeys.simulator(inputs, ticket),
    enabled: inputs != null,
    queryFn: () => apiPost<SimulatorResult>("/planning/simulate", inputs, { ticket }),
  });
}

export interface PowerBIReport {
  configured: boolean;
  embedUrl: string | null;
}

/** URL-based Power BI embed for the Strategy tab -- `configured: false` and
 * `embedUrl: null` until the backend's POWERBI_REPORT_URL env var is set
 * (no AAD token flow exists yet), same gap portfolio-risk-concentrations'
 * own Power BI integration has. */
export function usePowerBIReport() {
  return useQuery({
    queryKey: ["planning", "powerbi-report"],
    queryFn: () => apiGet<PowerBIReport>("/planning/powerbi-report"),
    staleTime: 5 * 60_000,
  });
}
